import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Input,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Card,
  Alert,
  Chip
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { BRAND } from '../brand';
import InstrumentPopup from '../components/InstrumentPopup';
import AddInstrumentModal from '../components/AddInstrumentModal';
import StockListCard from '../components/StockListCard';
import StockMobileList from '../components/StockMobileList';
import StockPageLayout from '../components/StockPageLayout';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import StockAddHeaderButton from '../components/StockAddHeaderButton';
import useIsMobile from '../hooks/useIsMobile';
import { encodeRouteParam } from '../util/routeParams';
import { formatDisplayDate } from '../util/formatDate';

const SORT_OPTIONS = {
  instrument_name: { label: 'Name', param: 'instrument_name' },
  total_quantity: { label: 'Total Qty', param: 'total_quantity' },
  vendor_name: { label: 'Vendor', param: 'vendor_name' },
  purchase_date: { label: 'Purchase Date', param: 'purchase_date' },
  brand: { label: 'Brand', param: 'brand' }
};

const StatusChips = ({ row }) => {
  const parts = [];
  if (Number(row.in_stock_qty) > 0) parts.push({ label: `${row.in_stock_qty} stock`, color: 'success' });
  if (Number(row.in_use_qty) > 0) parts.push({ label: `${row.in_use_qty} use`, color: 'primary' });
  if (Number(row.missed_qty) > 0) parts.push({ label: `${row.missed_qty} missed`, color: 'warning' });
  if (Number(row.broken_qty) > 0) parts.push({ label: `${row.broken_qty} broken`, color: 'danger' });
  if (Number(row.discarded_qty) > 0) parts.push({ label: `${row.discarded_qty} discarded`, color: 'neutral' });
  if (parts.length === 0) return <span>—</span>;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {parts.map((p) => (
        <Chip key={p.label} size="sm" color={p.color} variant="soft">{p.label}</Chip>
      ))}
    </Box>
  );
};

const Instruments = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('instrument_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder]);

  const fetchInstruments = useCallback(async () => {
    try {
      setLoading(true);
      const params = { sortBy, sortOrder };
      if (searchTerm) params.search = searchTerm;
      const response = await axiosInstance.get('/instruments', { params });
      setInstruments(response.data.instruments || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch instruments');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchInstruments();
  }, [fetchInstruments]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const headerAction = useMemo(
    () => (
      <StockAddHeaderButton
        label="Add instrument"
        onClick={() => setAddModalOpen(true)}
      />
    ),
    []
  );

  useSetPageHeaderAction(headerAction);

  const handleInstrumentClick = (instrumentName) => {
    if (isMobile) {
      navigate(`/instruments/${encodeRouteParam(instrumentName)}`);
      return;
    }
    setSelectedInstrument(instrumentName);
  };

  const SortableHeader = ({ column, children, style = {} }) => (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => handleSort(column)}
      title={`Sort by ${SORT_OPTIONS[column]?.label || column}`}
    >
      {children}
      {sortBy === column && (
        <span style={{ marginLeft: 4, opacity: 0.9 }}>
          {sortOrder === 'asc' ? ' ▲' : ' ▼'}
        </span>
      )}
    </th>
  );

  const totalPages = Math.ceil(instruments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = instruments.slice(startIndex, startIndex + itemsPerPage);

  const paginationBar = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography level="body-md">
        Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, instruments.length)} of {instruments.length} instruments
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
    <StockPageLayout
      error={error && <Alert color="danger">{error}</Alert>}
      loading={loading}
      search={(
        <Card sx={{ mb: { xs: 0, md: 3 }, p: 2 }}>
          <FormControl>
            <FormLabel>Search</FormLabel>
            <Input
              placeholder="Search by name, brand, make, serial..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FormControl>
        </Card>
      )}
      pagination={paginationBar}
    >
          <StockMobileList empty={paginated.length === 0} emptyMessage="No instruments found">
            {paginated.map((item) => (
              <StockListCard
                key={item.instrument_name}
                title={item.instrument_name}
                onClick={() => handleInstrumentClick(item.instrument_name)}
                fields={[
                  { label: 'Total Qty', value: item.total_quantity ?? 0 },
                  { label: 'Status', value: <StatusChips row={item} /> },
                  { label: 'Vendor', value: item.vendor_name || '—' },
                  {
                    label: 'Brand / Make',
                    value: [item.brand, item.make].filter(Boolean).join(' / ') || '—'
                  },
                  {
                    label: 'Earliest Purchase',
                    value: formatDisplayDate(item.earliest_purchase)
                  }
                ]}
              />
            ))}
          </StockMobileList>

          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              overflowX: 'auto'
            }}
          >
          <Table
            stickyHeader
            hoverRow
            sx={{
              '& thead th': { backgroundColor: 'primary.500', color: 'white' },
              '& tbody tr:nth-of-type(even)': { backgroundColor: 'background.level1' }
            }}
          >
            <thead>
              <tr>
                <SortableHeader column="instrument_name">Instrument Name</SortableHeader>
                <SortableHeader column="total_quantity">Total Qty</SortableHeader>
                <th>Status Breakdown</th>
                <SortableHeader column="vendor_name">Vendor</SortableHeader>
                <SortableHeader column="brand">Brand / Make</SortableHeader>
                <SortableHeader column="purchase_date">Earliest Purchase</SortableHeader>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                    No instruments found
                  </td>
                </tr>
              ) : (
                paginated.map((item) => (
                  <tr
                    key={item.instrument_name}
                    onClick={() => handleInstrumentClick(item.instrument_name)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <Typography sx={{ color: BRAND.colors.navy, fontWeight: 600 }}>
                        {item.instrument_name}
                      </Typography>
                    </td>
                    <td>{item.total_quantity ?? 0}</td>
                    <td><StatusChips row={item} /></td>
                    <td>{item.vendor_name || '—'}</td>
                    <td>
                      {[item.brand, item.make].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td>
                      {formatDisplayDate(item.earliest_purchase)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          </Box>
    </StockPageLayout>

      {selectedInstrument && (
        <InstrumentPopup
          instrumentName={selectedInstrument}
          onClose={() => {
            setSelectedInstrument(null);
            fetchInstruments();
          }}
        />
      )}

      <AddInstrumentModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={fetchInstruments}
      />
    </>
  );
};

export default Instruments;
